import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import adminService from '../services/adminService';
import { getLocalDateString } from '../utils/dateUtils';

const CategoryPieChart = ({ selectedMonth, externalData = null }) => {
    const [viewMode, setViewMode] = useState('month'); // 'day' | 'month'
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // If we are in 'month' mode and have externalData, use it!
        if (viewMode === 'month' && externalData && externalData.length > 0) {
            setData(externalData);
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                let startDate, endDate;

                if (viewMode === 'day') {
                    // Today - Use standardized local date string
                    startDate = getLocalDateString();
                    endDate = startDate + ' 23:59:59';
                } else {
                    // Selected Month
                    if (!selectedMonth) return;
                    const [year, month] = selectedMonth.split('-');
                    startDate = `${year}-${month}-01`;
                    endDate = new Date(year, month, 0).toLocaleDateString('en-CA') + ' 23:59:59';
                }

                const response = await adminService.getCategoryStats({ startDate, endDate });
                if (response.success) {
                    setData(response.data);
                }
            } catch (error) {
                console.error('Error loading category stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [viewMode, selectedMonth, externalData]);

    // Transform data for the chart
    const chartData = data?.map(cat => ({
        name: cat.category_group,
        value: Number(cat.sales_value),
        fullData: cat
    })).filter(item => item.value > 0) || [];

    // Sort by value desc
    chartData.sort((a, b) => b.value - a.value);

    // Color Palette Matching the List (Consistente con ExecutiveDashboardPage)
    const getColor = (name) => {
        const n = String(name || '').toUpperCase().trim();
        if (n.includes('LIQUIPOPS')) return '#ec4899'; // pink-500
        if (n.includes('GENIALITY')) return '#a855f7'; // purple-500
        if (n.includes('SKARCHA')) return '#f97316';   // orange-500
        if (n.includes('YEXIS')) return '#eab308';     // yellow-500
        if (n.includes('BUBOLS')) return '#dc2626'; // red-600
        if (n.includes('LIQUIMON') || n.includes('BASE')) return '#84cc16'; // lime-500
        if (n.includes('POLVO') || n.includes('MEZCLA')) return '#2563eb'; // blue-600
        if (n.includes('SIROPE')) return '#06b6d4';   // cyan-500
        if (n.includes('SALSA')) return '#d97706';    // amber-600
        if (n.includes('EMPAQUE')) return '#64748b';  // slate-500
        return '#9ca3af'; // gray-400 (Otros / Sin Clasificar)
    };


    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const percent = (data.value / chartData.reduce((acc, curr) => acc + curr.value, 0)) * 100;
            return (
                <div className="bg-white p-3 border border-gray-100 shadow-lg rounded-lg z-50">
                    <p className="font-bold text-gray-800">{data.name}</p>
                    <p className="text-sm text-gray-600">Ventas: {formatCurrency(data.value)}</p>
                    <p className="text-xs text-gray-400">Participación: {percent.toFixed(1)}%</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-full flex flex-col relative">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-bold text-gray-800">Distribución de Ventas (Mix)</h3>
                <div className="bg-gray-100 p-1 rounded-lg flex text-xs">
                    <button
                        onClick={() => setViewMode('day')}
                        className={`px-3 py-1 rounded-md transition-all ${viewMode === 'day' ? 'bg-white shadow text-gray-800 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Hoy
                    </button>
                    <button
                        onClick={() => setViewMode('month')}
                        className={`px-3 py-1 rounded-md transition-all ${viewMode === 'month' ? 'bg-white shadow text-gray-800 font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Mes
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-[300px]">
                {loading ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">Cargando...</div>
                ) : chartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">No hay ventas registradas</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius="75%"
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={getColor(entry.name)} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default CategoryPieChart;
